import React, { useCallback, useEffect, useState } from "react";
import {
  Table,
  Button,
  Modal,
  Input,
  Form,
  message,
  Tag,
  Space,
  Typography,
} from "antd";
import {
  EyeOutlined,
  EyeInvisibleOutlined,
  PlusOutlined,
  KeyOutlined,
  LinkOutlined,
  DisconnectOutlined,
  LogoutOutlined,
} from "@ant-design/icons";
import { API_URL } from "../../../environment";

const { Title, Text } = Typography;

// Sessao do gestor guardada em sessionStorage (some ao fechar a aba). Sem token —
// cada chamada reenvia login+senha e o backend revalida contra white_label_brands.
const SESSION_KEY = "__wl_admin_session_v1";

interface LoginRow {
  id_user: number;
  login: string;
  senha: string;
  id_time: number;
  id_cliente: number;
  admin_master: boolean | null;
  admin_time: boolean | null;
  white_label: string | null;
  nome: string | null;
}

interface Session {
  login: string;
  senha: string;
  slug: string;
  nome_produto?: string;
}

export default function WhiteLabelAdmin() {
  const [session, setSession] = useState<Session | null>(null);
  const [bootChecking, setBootChecking] = useState(true);

  // --- Login ---
  const [loginUser, setLoginUser] = useState("");
  const [loginPass, setLoginPass] = useState("");
  const [loginErr, setLoginErr] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);

  // --- Painel ---
  const [rows, setRows] = useState<LoginRow[]>([]);
  const [loadingRows, setLoadingRows] = useState(false);
  const [revealed, setRevealed] = useState<Set<number>>(new Set());

  // --- Modais ---
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm] = Form.useForm();
  const [creating, setCreating] = useState(false);

  const [pwdOpen, setPwdOpen] = useState(false);
  const [pwdTarget, setPwdTarget] = useState<LoginRow | null>(null);
  const [pwdValue, setPwdValue] = useState("");
  const [savingPwd, setSavingPwd] = useState(false);

  const [linkOpen, setLinkOpen] = useState(false);
  const [linkEmail, setLinkEmail] = useState("");
  const [linking, setLinking] = useState(false);

  // Recupera sessao salva ao montar.
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(SESSION_KEY);
      if (raw) setSession(JSON.parse(raw));
    } catch {
      /* ignore */
    }
    setBootChecking(false);
  }, []);

  const loadLogins = useCallback(async (sess: Session) => {
    setLoadingRows(true);
    try {
      const res = await fetch(`${API_URL}/wl-admin/logins`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ login: sess.login, senha: sess.senha }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || "Falha ao carregar logins");
      setRows(data.logins || []);
    } catch (e: any) {
      message.error(e?.message || "Erro ao carregar logins");
      if (String(e?.message || "").includes("autoriz")) handleLogout();
    } finally {
      setLoadingRows(false);
    }
  }, []);

  useEffect(() => {
    if (session) loadLogins(session);
  }, [session, loadLogins]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginErr("");
    setLoggingIn(true);
    try {
      const res = await fetch(`${API_URL}/wl-admin/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ login: loginUser.trim(), senha: loginPass }),
      });
      const data = await res.json();
      if (!res.ok) {
        setLoginErr(data?.error || "Login ou senha inválidos.");
        return;
      }
      const sess: Session = {
        login: loginUser.trim(),
        senha: loginPass,
        slug: data.slug,
        nome_produto: data.nome_produto,
      };
      sessionStorage.setItem(SESSION_KEY, JSON.stringify(sess));
      setSession(sess);
      setLoginPass("");
    } catch {
      setLoginErr("Erro de conexão. Tente novamente.");
    } finally {
      setLoggingIn(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem(SESSION_KEY);
    setSession(null);
    setRows([]);
    setRevealed(new Set());
  };

  const toggleReveal = (id: number) => {
    setRevealed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const submitCreate = async () => {
    if (!session) return;
    try {
      const vals = await createForm.validateFields();
      setCreating(true);
      const res = await fetch(`${API_URL}/wl-admin/login/criar`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          login: session.login,
          senha: session.senha,
          nome: vals.nome,
          novo_login: vals.novo_login,
          nova_senha: vals.nova_senha,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || "Falha ao criar login");
      message.success("Login criado.");
      setCreateOpen(false);
      createForm.resetFields();
      loadLogins(session);
    } catch (e: any) {
      if (e?.errorFields) return; // erro de validacao do form
      message.error(e?.message || "Erro ao criar login");
    } finally {
      setCreating(false);
    }
  };

  const submitPwd = async () => {
    if (!session || !pwdTarget) return;
    if (!pwdValue) {
      message.warning("Informe a nova senha.");
      return;
    }
    setSavingPwd(true);
    try {
      const res = await fetch(`${API_URL}/wl-admin/login/senha`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          login: session.login,
          senha: session.senha,
          id_user: pwdTarget.id_user,
          nova_senha: pwdValue,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || "Falha ao alterar senha");
      message.success("Senha atualizada.");
      setPwdOpen(false);
      setPwdValue("");
      setPwdTarget(null);
      loadLogins(session);
    } catch (e: any) {
      message.error(e?.message || "Erro ao alterar senha");
    } finally {
      setSavingPwd(false);
    }
  };

  const submitLink = async () => {
    if (!session) return;
    if (!linkEmail.trim()) {
      message.warning("Informe o e-mail do login.");
      return;
    }
    setLinking(true);
    try {
      const res = await fetch(`${API_URL}/wl-admin/login/vinculo`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          login: session.login,
          senha: session.senha,
          acao: "vincular",
          alvo_login: linkEmail.trim(),
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || "Falha ao vincular");
      message.success("Login vinculado.");
      setLinkOpen(false);
      setLinkEmail("");
      loadLogins(session);
    } catch (e: any) {
      message.error(e?.message || "Erro ao vincular login");
    } finally {
      setLinking(false);
    }
  };

  const handleUnlink = (row: LoginRow) => {
    if (!session) return;
    Modal.confirm({
      title: "Desvincular login?",
      content: `"${row.login}" deixará de ter acesso a este white-label (volta a ser login base).`,
      okText: "Desvincular",
      okButtonProps: { danger: true },
      cancelText: "Cancelar",
      onOk: async () => {
        const res = await fetch(`${API_URL}/wl-admin/login/vinculo`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            login: session.login,
            senha: session.senha,
            acao: "desvincular",
            id_user: row.id_user,
          }),
        });
        const data = await res.json();
        if (!res.ok) {
          message.error(data?.error || "Falha ao desvincular");
          return;
        }
        message.success("Login desvinculado.");
        loadLogins(session);
      },
    });
  };

  const columns = [
    {
      title: "Login (e-mail)",
      dataIndex: "login",
      key: "login",
    },
    {
      title: "Nome",
      dataIndex: "nome",
      key: "nome",
      render: (v: string | null) => v || <Text type="secondary">—</Text>,
    },
    {
      title: "Senha",
      dataIndex: "senha",
      key: "senha",
      render: (v: string, row: LoginRow) => (
        <Space>
          <span style={{ fontFamily: "monospace" }}>
            {revealed.has(row.id_user) ? v : "••••••••"}
          </span>
          <Button
            size="small"
            type="text"
            icon={revealed.has(row.id_user) ? <EyeInvisibleOutlined /> : <EyeOutlined />}
            onClick={() => toggleReveal(row.id_user)}
          />
        </Space>
      ),
    },
    {
      title: "Tipo",
      key: "tipo",
      render: (_: any, row: LoginRow) =>
        row.admin_master ? (
          <Tag color="gold">Admin master</Tag>
        ) : row.admin_time ? (
          <Tag color="blue">Admin time</Tag>
        ) : (
          <Tag>Usuário</Tag>
        ),
    },
    {
      title: "Ações",
      key: "acoes",
      render: (_: any, row: LoginRow) => (
        <Space>
          <Button
            size="small"
            icon={<KeyOutlined />}
            onClick={() => {
              setPwdTarget(row);
              setPwdValue("");
              setPwdOpen(true);
            }}
          >
            Senha
          </Button>
          <Button
            size="small"
            danger
            icon={<DisconnectOutlined />}
            onClick={() => handleUnlink(row)}
          >
            Desvincular
          </Button>
        </Space>
      ),
    },
  ];

  if (bootChecking) return null;

  // ---------- Tela de LOGIN ----------
  if (!session) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "#f4f6fa",
          padding: 16,
        }}
      >
        <form
          onSubmit={handleLogin}
          style={{
            width: 360,
            maxWidth: "100%",
            background: "#fff",
            borderRadius: 12,
            padding: 28,
            boxShadow: "0 8px 28px rgba(0,0,0,0.10)",
          }}
        >
          <Title level={4} style={{ marginBottom: 4 }}>
            Painel White-Label
          </Title>
          <Text type="secondary" style={{ display: "block", marginBottom: 20 }}>
            Acesso do gestor
          </Text>
          <div style={{ marginBottom: 12 }}>
            <Text>Login</Text>
            <Input
              size="large"
              value={loginUser}
              onChange={(e) => setLoginUser(e.target.value)}
              autoFocus
            />
          </div>
          <div style={{ marginBottom: 16 }}>
            <Text>Senha</Text>
            <Input.Password
              size="large"
              value={loginPass}
              onChange={(e) => setLoginPass(e.target.value)}
            />
          </div>
          {loginErr && (
            <div style={{ color: "#d4380d", marginBottom: 12 }}>{loginErr}</div>
          )}
          <Button
            type="primary"
            htmlType="submit"
            size="large"
            block
            loading={loggingIn}
          >
            Entrar
          </Button>
        </form>
      </div>
    );
  }

  // ---------- PAINEL ----------
  return (
    <div style={{ minHeight: "100vh", background: "#f4f6fa", padding: 24 }}>
      <div style={{ maxWidth: 1080, margin: "0 auto" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 20,
            flexWrap: "wrap",
            gap: 12,
          }}
        >
          <div>
            <Title level={3} style={{ margin: 0 }}>
              {session.nome_produto || "White-Label"}
            </Title>
            <Text type="secondary">
              Logins vinculados · slug <Text code>{session.slug}</Text>
            </Text>
          </div>
          <Space>
            <Button icon={<LinkOutlined />} onClick={() => setLinkOpen(true)}>
              Vincular existente
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setCreateOpen(true)}
            >
              Novo login
            </Button>
            <Button icon={<LogoutOutlined />} onClick={handleLogout}>
              Sair
            </Button>
          </Space>
        </div>

        <Table
          rowKey="id_user"
          columns={columns as any}
          dataSource={rows}
          loading={loadingRows}
          pagination={{ pageSize: 20, hideOnSinglePage: true }}
          locale={{ emptyText: "Nenhum login vinculado a este white-label." }}
          style={{ background: "#fff", borderRadius: 12, padding: 8 }}
        />
      </div>

      {/* Modal: criar login */}
      <Modal
        title="Novo login"
        open={createOpen}
        onCancel={() => setCreateOpen(false)}
        onOk={submitCreate}
        okText="Criar"
        confirmLoading={creating}
        destroyOnClose
      >
        <Form form={createForm} layout="vertical" preserve={false}>
          <Form.Item
            name="nome"
            label="Nome"
            rules={[{ required: true, message: "Informe o nome" }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="novo_login"
            label="Login (e-mail)"
            rules={[{ required: true, message: "Informe o e-mail" }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="nova_senha"
            label="Senha"
            rules={[{ required: true, message: "Informe a senha" }]}
          >
            <Input.Password />
          </Form.Item>
          <Text type="secondary">
            O login já nasce vinculado a este white-label.
          </Text>
        </Form>
      </Modal>

      {/* Modal: alterar senha */}
      <Modal
        title={pwdTarget ? `Alterar senha — ${pwdTarget.login}` : "Alterar senha"}
        open={pwdOpen}
        onCancel={() => setPwdOpen(false)}
        onOk={submitPwd}
        okText="Salvar"
        confirmLoading={savingPwd}
        destroyOnClose
      >
        <Text>Nova senha</Text>
        <Input.Password
          value={pwdValue}
          onChange={(e) => setPwdValue(e.target.value)}
          autoFocus
        />
      </Modal>

      {/* Modal: vincular existente */}
      <Modal
        title="Vincular login existente"
        open={linkOpen}
        onCancel={() => setLinkOpen(false)}
        onOk={submitLink}
        okText="Vincular"
        confirmLoading={linking}
        destroyOnClose
      >
        <Text>E-mail do login (precisa ser um login base, sem white-label)</Text>
        <Input
          value={linkEmail}
          onChange={(e) => setLinkEmail(e.target.value)}
          placeholder="exemplo@dominio.com"
          autoFocus
        />
      </Modal>
    </div>
  );
}
